import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  Plus,
  ChevronRight,
  ArrowUpRight,
  Sparkles,
  Brain,
  Lock,
  Leaf,
  Filter as FilterIcon,
  FileUp,
  FileCheck,
  UserPlus,
  Film as FilmIcon,
  Camera,
  Settings,
  Users,
  Crown,
  AlertTriangle,
  Activity as ActivityIcon,
  CalendarDays,
  Loader,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { useFeatures } from '../context/FeatureContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useFilm } from '../context/FilmContext';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceFilms, useWorkspaceUsage, useWorkspaceActivity, apiGet, qk } from '../hooks/queries';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { EVENTS, track } from '../lib/analytics';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import UpgradeWorkspaceModal from '../components/UpgradeWorkspaceModal';
import BillingAlertBanner from '../components/BillingAlertBanner';
import { API_BASE } from '../config/api';
import { cn } from '../lib/utils';

/* ----------------------------- helpers ----------------------------- */

const gradeFromCoverage = (pct) => {
  if (pct == null || isNaN(pct)) return { letter: '—', tone: 'muted', tagline: 'Awaiting data' };
  if (pct >= 90) return { letter: 'A',  tone: 'success', tagline: 'Optimal Production Flow' };
  if (pct >= 75) return { letter: 'B',  tone: 'success', tagline: 'Optimal Production Flow' };
  if (pct >= 50) return { letter: 'C',  tone: 'warning', tagline: 'Needs Attention' };
  if (pct >= 25) return { letter: 'D',  tone: 'destructive', tagline: 'Critical' };
  return            { letter: 'E',  tone: 'destructive', tagline: 'Critical' };
};

const toneClasses = {
  success:     { text: 'text-success',     bgSoft: 'bg-success/10' },
  warning:     { text: 'text-warning',     bgSoft: 'bg-warning/10' },
  destructive: { text: 'text-destructive', bgSoft: 'bg-destructive/10' },
  muted:       { text: 'text-muted-foreground', bgSoft: 'bg-muted/40' },
};

const formatRelativeShort = (iso) => {
  if (!iso) return '';
  try {
    const r = formatDistanceToNowStrict(new Date(iso), { addSuffix: false });
    // Convert "5 minutes" → "5m", "2 hours" → "2h", "1 day" → "1d"
    const map = [
      [/^less than a minute$/, 'JUST NOW'],
      [/^a few seconds$/,      'JUST NOW'],
      [/(\d+)\s+seconds?$/,    '$1S AGO'],
      [/^1 minute$/,           '1M AGO'],
      [/(\d+)\s+minutes?$/,    '$1M AGO'],
      [/^1 hour$/,             '1H AGO'],
      [/(\d+)\s+hours?$/,      '$1H AGO'],
      [/^1 day$/,              '1D AGO'],
      [/(\d+)\s+days?$/,       '$1D AGO'],
      [/(\d+)\s+months?$/,     '$1MO AGO'],
      [/(\d+)\s+years?$/,      '$1Y AGO'],
    ];
    for (const [re, rep] of map) {
      if (re.test(r)) return r.replace(re, rep).toUpperCase();
    }
    return r.toUpperCase();
  } catch {
    return '';
  }
};

const titleInitial = (t = '') => (t.trim() ? t.trim()[0].toUpperCase() : '?');

/* --------------------------- KPI tiles --------------------------- */

const KpiTile = ({ children, testId }) => (
  <div
    className="rounded-2xl border border-border bg-card p-5 flex flex-col h-full min-h-[150px]"
    data-testid={testId}
  >
    {children}
  </div>
);

const KpiFilms = ({ usage, planName, retry }) => {
  const films = usage?.films;
  const limit = films?.limit;
  const current = films?.current ?? 0;
  const unlimited = limit === -1;
  const limitDisplay = unlimited ? '∞' : limit;
  const pct = unlimited
    ? Math.min(100, Math.max(2, current ? 30 : 2))
    : limit && limit > 0
      ? Math.min(100, Math.round((current / limit) * 100))
      : 0;

  const errored = !films && retry;

  return (
    <KpiTile testId="wh-kpi-films">
      <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
        Films Used
      </p>
      <div className="flex items-baseline gap-2 mt-2">
        {errored ? (
          <span className="font-display text-4xl font-semibold leading-none">—</span>
        ) : (
          <span className="font-display text-4xl font-semibold leading-none text-foreground">
            {current}
          </span>
        )}
        <span className="text-sm text-muted-foreground font-medium">/ {limitDisplay}</span>
      </div>
      <div className="mt-3 h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${Math.max(pct, 4)}%`, opacity: pct === 0 ? 0.4 : 1 }}
        />
      </div>
      <div className="mt-auto pt-3 flex items-center justify-between">
        <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
          Of {planName || 'Free'} Plan
        </p>
        {errored && (
          <Button variant="ghost" size="sm" onClick={retry} className="h-7 px-2 text-xs">
            Retry
          </Button>
        )}
      </div>
    </KpiTile>
  );
};

const KpiCoverage = ({ films }) => {
  const totals = useMemo(() => {
    let shots = 0, covered = 0;
    for (const f of films || []) {
      shots += Number(f.shot_count) || 0;
      covered += Number(f.covered_shot_count) || 0;
    }
    const pct = shots > 0 ? Math.round((covered / shots) * 100) : null;
    return { shots, covered, pct };
  }, [films]);

  const grade = gradeFromCoverage(totals.pct);
  const tone = toneClasses[grade.tone] || toneClasses.muted;

  return (
    <KpiTile testId="wh-kpi-coverage">
      <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
        Coverage Health
      </p>
      <div className="flex items-baseline gap-3 mt-2">
        <span className="font-display text-4xl font-semibold leading-none text-foreground">
          {totals.pct == null ? '—' : `${totals.pct}%`}
        </span>
        <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-sm font-bold', tone.text, tone.bgSoft)}>
          {grade.letter}
          <Leaf className="size-3.5" aria-hidden />
        </span>
      </div>
      <div className="mt-3 h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all',
            grade.tone === 'success' ? 'bg-success' :
            grade.tone === 'warning' ? 'bg-warning' :
            grade.tone === 'destructive' ? 'bg-destructive' : 'bg-muted-foreground/30'
          )}
          style={{ width: `${Math.max(totals.pct ?? 4, 4)}%`, opacity: totals.pct == null ? 0.4 : 1 }}
        />
      </div>
      <div className="mt-auto pt-3">
        <p className={cn('text-[11px] font-semibold tracking-[0.18em] uppercase', tone.text)}>
          {grade.tagline}
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {totals.covered} / {totals.shots} shots covered
        </p>
      </div>
    </KpiTile>
  );
};

/* KpiTeamActivity — 3rd tile in the strip.
 * Surfaces workspace-level activity volume + member count, derived purely
 * from data we already fetch:
 *   - `members` count from the workspace object (`member_count`)
 *   - `events` from /api/workspaces/:id/activity (last 7 days bucketed)
 * No new backend endpoints required.
 */
const KpiTeamActivity = ({ memberCount, events, loading }) => {
  const stats = useMemo(() => {
    const now = Date.now();
    const sevenDaysMs = 7 * 86400000;
    let recent = 0;
    let lastIso = null;
    for (const ev of events || []) {
      const t = new Date(ev.created_at).getTime();
      if (!Number.isFinite(t)) continue;
      if (now - t <= sevenDaysMs) recent += 1;
      if (!lastIso || t > new Date(lastIso).getTime()) lastIso = ev.created_at;
    }
    return { recent, lastIso };
  }, [events]);

  // Cap the visual fill at 20 events/week → 100% bar
  const pct = Math.min(100, Math.round((stats.recent / 20) * 100));
  const lastRelative = stats.lastIso ? formatRelativeShort(stats.lastIso) : null;

  return (
    <KpiTile testId="wh-kpi-team">
      <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
        Team Activity
      </p>
      <div className="flex items-baseline gap-2 mt-2">
        {loading ? (
          <span className="font-display text-4xl font-semibold leading-none text-muted-foreground">—</span>
        ) : (
          <span className="font-display text-4xl font-semibold leading-none text-foreground">
            {stats.recent}
          </span>
        )}
        <span className="text-sm text-muted-foreground font-medium">events / 7d</span>
      </div>
      <div className="mt-3 h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-info transition-all"
          style={{ width: `${Math.max(pct, 4)}%`, opacity: pct === 0 ? 0.4 : 1 }}
        />
      </div>
      <div className="mt-auto pt-3 flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
          {memberCount != null ? `${memberCount} ${memberCount === 1 ? 'member' : 'members'}` : 'Members'}
        </p>
        {lastRelative && (
          <p className="text-[10px] text-muted-foreground" data-testid="wh-kpi-team-last">
            Last: {lastRelative}
          </p>
        )}
      </div>
    </KpiTile>
  );
};

/* --------------------------- Films slate --------------------------- */

const ProductionCard = ({ film, workspaceId, onRemoveSample }) => {
  const pct = (film.shot_count > 0)
    ? Math.round((film.covered_shot_count / film.shot_count) * 100)
    : 0;

  return (
    <div className="relative">
    <Link
      to={`/workspaces/${workspaceId}/films/${film.id}`}
      data-testid={`wh-production-${film.id}`}
      className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-border bg-card hover:border-primary/40 transition-all hover:-translate-y-0.5 hover:shadow-lg"
    >
      {/* Background */}
      {film.poster_url ? (
        <img
          src={film.poster_url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/40 to-warning/30 flex items-center justify-center">
          <span className="font-display text-7xl font-bold text-white/30 select-none">
            {titleInitial(film.title)}
          </span>
        </div>
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

      {/* Bottom-left text */}
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <p className="text-[10px] font-semibold tracking-[0.22em] text-primary/90 uppercase mb-1.5">
          {film.is_sample ? 'Sample film · explore, then delete' : 'Film'}
        </p>
        <h3 className="font-display text-xl md:text-2xl font-semibold text-white leading-tight tracking-tight line-clamp-2">
          {film.title}
        </h3>
      </div>

      {/* Bottom-right pill */}
      <div className="absolute top-4 right-4">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-[0.18em] uppercase bg-black/55 text-white backdrop-blur-sm">
          {pct}% Complete
        </span>
      </div>
    </Link>
    {/* Sample film (M0.4): one-click removal, outside the Link so it doesn't navigate. */}
    {film.is_sample && onRemoveSample && (
      <button
        type="button"
        onClick={() => onRemoveSample(film)}
        data-testid={`wh-remove-sample-${film.id}`}
        title="Delete sample film"
        className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-[0.18em] uppercase bg-black/55 text-white/90 backdrop-blur-sm hover:bg-destructive hover:text-white transition-colors"
      >
        <Trash2 className="size-3" /> Remove
      </button>
    )}
    </div>
  );
};

const NewProductionCard = ({ onClick, atLimit }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="wh-new-production"
    className={cn(
      'aspect-[4/3] rounded-2xl border border-dashed flex flex-col items-center justify-center gap-3 transition-all',
      atLimit
        ? 'border-border/60 bg-card/50 text-muted-foreground cursor-pointer hover:border-warning/50'
        : 'border-border bg-card/60 hover:border-primary/40 hover:bg-card hover:-translate-y-0.5 hover:shadow-lg'
    )}
  >
    <span className="size-14 rounded-2xl bg-secondary flex items-center justify-center">
      {atLimit ? <Lock className="size-6 text-muted-foreground" /> : <Plus className="size-6 text-primary" />}
    </span>
    <h3 className="font-display font-semibold text-xl tracking-tight">
      {atLimit ? 'Plan limit reached' : 'New Production'}
    </h3>
    <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground">
      {atLimit ? 'Upgrade to add more' : 'Initialize Studio Canvas'}
    </p>
  </button>
);

/* --------------------------- AI Risk Insights ---------------------------
 *
 * Fetches insights from GET /api/ai/insights/workspace/{id}?limit=3
 * and displays the top 3 items. Links to /ai-insights for the full view.
 */

const SEVERITY_TOKENS = {
  critical:    { stripe: 'bg-destructive', text: 'text-destructive' },
  warn:        { stripe: 'bg-warning',     text: 'text-warning' },
  warning:     { stripe: 'bg-warning',     text: 'text-warning' },
  info:        { stripe: 'bg-info',        text: 'text-info' },
};

const InsightItem = ({ insight }) => {
  const tokens = SEVERITY_TOKENS[insight.severity] || SEVERITY_TOKENS.info;
  return (
    <Link
      to="/ai-insights"
      className="block relative pl-4 py-3 pr-2 rounded-lg hover:bg-muted/50 transition-colors"
    >
      <span aria-hidden className={cn('absolute left-0 top-2 bottom-2 w-1 rounded-full', tokens.stripe)} />
      <p className={cn('text-[10px] font-semibold tracking-[0.22em] uppercase', tokens.text)}>
        {insight.category?.toUpperCase()} {insight.entity_ref?.name ? <><span className="text-muted-foreground">·</span> {insight.entity_ref.name}</> : ''}
      </p>
      <p className="mt-1.5 text-sm text-foreground leading-snug">
        {insight.body}
      </p>
    </Link>
  );
};

const AIInsightsCard = ({ allowed, insights, insightsLoading, films }) => {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 flex flex-col" data-testid="wh-ai-insights">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className="size-9 rounded-xl bg-warning/15 text-warning flex items-center justify-center">
            <Brain className="size-5" />
          </span>
          <h2 className="font-display text-2xl font-bold tracking-tight">AI Risk Insights</h2>
        </div>
        <Button asChild variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label="View all insights">
          <Link to="/ai-insights">
            <FilterIcon className="size-4" />
          </Link>
        </Button>
      </div>

      {!allowed ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-8 px-4">
          <span className="size-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <Lock className="size-6 text-muted-foreground" />
          </span>
          <h3 className="font-display text-xl font-semibold tracking-tight mb-1">
            Unlock AI Risk Insights
          </h3>
          <p className="text-sm text-muted-foreground max-w-sm mb-5">
            Upgrade to Pro for predictive risk analysis on your productions —
            schedule, coverage, and resourcing alerts surfaced automatically.
          </p>
          <Button asChild size="sm" className="rounded-xl" data-testid="wh-ai-upgrade-cta">
            <Link to="/billing">
              <Crown className="size-4 mr-1.5" /> Upgrade to Pro
            </Link>
          </Button>
        </div>
      ) : insightsLoading ? (
        <div className="flex-1 flex items-center justify-center py-8">
          <Loader className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : insights.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-8 px-4">
          <span className="size-12 rounded-2xl bg-success/15 text-success flex items-center justify-center mb-3">
            <Sparkles className="size-5" />
          </span>
          <p className="text-sm font-medium text-foreground">No active risks detected</p>
          <p className="text-xs text-muted-foreground mt-1">
            {films?.length === 0
              ? 'Add a film to start surfacing insights.'
              : 'Production looks healthy. We\u2019ll flag anything that drifts.'}
          </p>
        </div>
      ) : (
        <>
          <div className="flex-1 space-y-1">
            {insights.map((ins) => (
              <InsightItem key={ins.id} insight={ins} />
            ))}
          </div>
          <Button
            asChild
            variant="secondary"
            size="sm"
            className="mt-5 w-full rounded-xl tracking-[0.16em] uppercase text-[11px] font-semibold"
            data-testid="wh-ai-full-report"
          >
            <Link to="/ai-insights">View All Insights</Link>
          </Button>
        </>
      )}
    </div>
  );
};

/* --------------------------- Recent Activity --------------------------- */

const ICON_FOR_TYPE = {
  scene_created:        FileCheck,
  scene_status_changed: FileCheck,
  shot_created:         Camera,
  shot_covered:         FileCheck,
  film_created:         FilmIcon,
  film_updated:         FilmIcon,
  member_joined:        UserPlus,
  script_imported:      FileUp,
};

const ActivityRow = ({ event }) => {
  const Icon = ICON_FOR_TYPE[event.type] || ActivityIcon;
  return (
    <div className="flex items-start gap-3 py-3" data-testid={`wh-activity-row-${event.type}`}>
      <span className="size-10 rounded-xl bg-muted flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="size-4 text-muted-foreground" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground leading-snug line-clamp-2">{event.message}</p>
      </div>
      <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground shrink-0 mt-1.5 ml-1">
        {formatRelativeShort(event.created_at)}
      </span>
    </div>
  );
};

const ActivityCard = ({ events, loading, error }) => {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 flex flex-col" data-testid="wh-recent-activity">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display text-2xl font-bold tracking-tight">Recent Activity</h2>
      </div>

      <div className="flex-1 max-h-[500px] overflow-y-auto -mx-2 px-2 divide-y divide-border/60">
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-3 py-3">
              <Skeleton className="size-10 rounded-xl shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <Skeleton className="h-3 w-12 shrink-0" />
            </div>
          ))
        ) : error ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">Couldn&rsquo;t load activity.</p>
          </div>
        ) : !events.length ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">No recent activity yet.</p>
          </div>
        ) : (
          events.map((e) => <ActivityRow key={e.id} event={e} />)
        )}
      </div>
    </div>
  );
};

/* --------------------------- Workspace identity bar ---------------------------
 *
 * Replaces the previous "60-px-of-empty-space + lonely H1" hero with a
 * compact horizontal band:
 *   [LOGO]  Workspace Name  [PLAN]                              [Members] [Settings]
 *           5 members · 1 film · Created Apr 2026 · "description"
 *
 * Renders with `items-stretch` parent OK — bar is short (≈80–96px) so it
 * doesn't stretch awkwardly next to the KPI strip below.
 */

const formatCreatedAt = (iso) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch { return null; }
};

const IdentityBar = ({ workspace, planName, memberCount, filmCount, onMembers, onSettings, onCalendar, canEditSettings }) => {
  const created = formatCreatedAt(workspace?.created_at);
  const description = workspace?.description?.trim() || null;
  const isPro = (workspace?.plan_id || 'plan_free') !== 'plan_free';

  return (
    <section
      className="mb-8 rounded-2xl border border-border bg-card px-5 py-4 md:px-6 md:py-5"
      data-testid="wh-identity-bar"
    >
      <div className="flex items-center gap-4 md:gap-5">
        {/* Avatar / logo */}
        {workspace?.logo_url ? (
          <img
            src={workspace.logo_url}
            alt=""
            className="size-12 md:size-14 rounded-xl object-cover shrink-0 ring-1 ring-border"
            data-testid="wh-identity-logo"
          />
        ) : (
          <div
            aria-hidden
            className="size-12 md:size-14 rounded-xl shrink-0 flex items-center justify-center bg-gradient-to-br from-primary/30 to-warning/30 ring-1 ring-border"
            data-testid="wh-identity-initial"
          >
            <span className="font-display font-bold text-xl md:text-2xl text-foreground/80">
              {titleInitial(workspace?.name)}
            </span>
          </div>
        )}

        {/* Name + plan + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 md:gap-2.5 flex-wrap">
            <h1
              className="font-display font-bold tracking-tight text-2xl md:text-3xl leading-tight truncate"
              data-testid="wh-hero-title"
            >
              {workspace?.name || 'Workspace'}
            </h1>
            <span
              className={cn(
                'inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold tracking-[0.22em] uppercase',
                isPro
                  ? 'bg-warning text-warning-foreground shadow-sm'
                  : 'border border-border bg-card text-muted-foreground'
              )}
              data-testid="wh-plan-badge"
            >
              {planName || 'Free'}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">
            {memberCount != null && (
              <span className="inline-flex items-center gap-1">
                <Users className="size-3.5" aria-hidden />
                {memberCount} {memberCount === 1 ? 'member' : 'members'}
              </span>
            )}
            {memberCount != null && filmCount != null && <span className="text-muted-foreground/40">·</span>}
            {filmCount != null && (
              <span className="inline-flex items-center gap-1">
                <FilmIcon className="size-3.5" aria-hidden />
                {filmCount} {filmCount === 1 ? 'film' : 'films'}
              </span>
            )}
            {created && <span className="text-muted-foreground/40">·</span>}
            {created && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="size-3.5" aria-hidden />
                Created {created}
              </span>
            )}
            {description && (
              <>
                <span className="text-muted-foreground/40 hidden sm:inline">·</span>
                <span className="normal-case font-normal tracking-normal text-muted-foreground truncate max-w-[36ch] sm:max-w-[52ch]" title={description}>
                  {description}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onCalendar}
            className="rounded-xl gap-1.5 h-9"
            data-testid="wh-action-calendar"
          >
            <CalendarDays className="size-4" />
            <span className="hidden md:inline">Calendar</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onMembers}
            className="rounded-xl gap-1.5 h-9"
            data-testid="wh-action-members"
          >
            <Users className="size-4" />
            <span className="hidden md:inline">Members</span>
          </Button>
          {canEditSettings && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSettings}
              className="rounded-xl gap-1.5 h-9"
              data-testid="wh-action-settings"
            >
              <Settings className="size-4" />
              <span className="hidden md:inline">Settings</span>
            </Button>
          )}
        </div>
      </div>

      {/* Mobile-only action row (so the bar isn't too cramped on narrow viewports) */}
      <div className="sm:hidden flex items-center gap-2 mt-3 -mx-1">
        <Button
          variant="outline"
          size="sm"
          onClick={onCalendar}
          className="flex-1 rounded-xl gap-1.5 h-9"
        >
          <CalendarDays className="size-4" /> Calendar
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onMembers}
          className="flex-1 rounded-xl gap-1.5 h-9"
        >
          <Users className="size-4" /> Members
        </Button>
        {canEditSettings && (
          <Button
            variant="outline"
            size="sm"
            onClick={onSettings}
            className="flex-1 rounded-xl gap-1.5 h-9"
          >
            <Settings className="size-4" /> Settings
          </Button>
        )}
      </div>
    </section>
  );
};

/* --------------------------- Page --------------------------- */

const WorkspaceHome = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  useAuth();
  const { features, hasFeatureFlag } = useFeatures() || {};
  const { workspaces, selectWorkspace, getWorkspaceFeatures } = useWorkspace();
  const { fetchFilms: refreshFilmContext, invalidateFilms } = useFilm() || {};
  const queryClient = useQueryClient();

  const [workspace, setWorkspace] = useState(null);
  const [wsFeatures, setWsFeatures] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newFilmTitle, setNewFilmTitle] = useState('');
  const [newFilmDescription, setNewFilmDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Load workspace-scoped features (plan capabilities + overrides). This is the
  // source of truth for entitlement gating like `ai_insights`, `script_upload`,
  // etc. `useFeatures()` reads the user-level subscriptions record which is a
  // legacy path and is NOT authoritative for workspace-scoped features.
  useEffect(() => {
    if (!workspaceId || !getWorkspaceFeatures) return;
    let cancelled = false;
    (async () => {
      const f = await getWorkspaceFeatures(workspaceId);
      if (!cancelled) setWsFeatures(f);
    })();
    return () => { cancelled = true; };
  }, [workspaceId, getWorkspaceFeatures]);

  /* ----- data fetching (TanStack Query — [H]) ----- */

  const filmsQuery = useWorkspaceFilms(workspaceId);
  const usageQuery = useWorkspaceUsage(workspaceId);
  const activityQuery = useWorkspaceActivity(workspaceId, 20);

  const films = useMemo(() => filmsQuery.data || [], [filmsQuery.data]);
  const usage = usageQuery.data ?? null;
  const usageError = usageQuery.isError;
  const activity = useMemo(() => activityQuery.data || [], [activityQuery.data]);
  const activityLoading = activityQuery.isLoading;
  const activityError = activityQuery.isError;
  const loading = filmsQuery.isLoading || usageQuery.isLoading;

  // Risk-heuristics: fetch the top-4 films' scenes (need due_date/status, which
  // the workspace film payload omits). Dynamic set of queries via useQueries —
  // RQ dedups/caches them and shares the cache with the Scene pages.
  const top4Films = useMemo(() => films.slice(0, 4), [films]);
  const sceneQueries = useQueries({
    queries: top4Films.map((f) => ({
      queryKey: qk.filmScenes(f.id),
      queryFn: ({ signal }) => apiGet(`/films/${f.id}/scenes`, signal),
      enabled: !!f.id,
    })),
  });
  const sceneSignal = sceneQueries.map((q) => `${q.dataUpdatedAt}`).join('|');
  const scenesByFilmId = useMemo(() => {
    const out = {};
    top4Films.forEach((f, i) => { out[f.id] = sceneQueries[i]?.data || []; });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneSignal, top4Films]);

  // Resolve the current workspace (from context, else fetch) + sync selection.
  useEffect(() => {
    if (!workspaceId) return undefined;
    const controller = new AbortController();
    const wsCtx = workspaces.find((w) => w.id === workspaceId);
    if (wsCtx) {
      setWorkspace(wsCtx);
      selectWorkspace(wsCtx);
      return undefined;
    }
    // Fallback fetch if the context didn't have it yet. Access errors (404/403)
    // are handled by the films-query effect below.
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/workspaces/${workspaceId}`, { headers: getAuthHeaders(), signal: controller.signal });
        if (controller.signal.aborted) return;
        setWorkspace(r.data);
        selectWorkspace(r.data);
      } catch (err) {
        // Workspace metadata fetch failed — the films-query effect will
        // surface 404/403 via the dedicated error handler below.
        if (import.meta.env.DEV) console.warn('[WorkspaceHome] workspace fetch failed:', err);
      }
    })();
    return () => controller.abort();
  }, [workspaceId, workspaces, selectWorkspace]);

  // Redirect on access errors surfaced by the films query (404 / 403).
  useEffect(() => {
    const status = filmsQuery.error?.response?.status;
    if (status === 404) {
      toast.error('Workspace not found');
      navigate('/');
    } else if (status === 403) {
      toast.error('You do not have access to this workspace');
      navigate('/');
    }
  }, [filmsQuery.error, navigate]);

  /* ----- derivations ----- */

  const aiInsightsAllowed = useMemo(() => {
    // Authoritative check: workspace-scoped features (from
    // GET /api/workspaces/{id}/features → plan + overrides).
    if (wsFeatures) return Boolean(wsFeatures.ai_insights);
    // Fallbacks while wsFeatures is still loading:
    // 1) explicit feature flag toggle (staff-controlled)
    if (hasFeatureFlag?.('ai_insights')) return true;
    // 2) legacy user-level features payload (kept for back-compat)
    if (features?.ai_insights === true) return true;
    // 3) plan-tier inference from the workspace record (avoids flash of
    //    "locked" state on paid workspaces before /features resolves).
    const pid = workspace?.plan_id || 'plan_free';
    return pid !== 'plan_free';
  }, [wsFeatures, hasFeatureFlag, features, workspace?.plan_id]);

  // --- AI Insights from API (replaces client-side heuristics) ---
  const insightsQuery = useQuery({
    queryKey: qk.workspaceInsights(workspaceId, 3),
    queryFn: ({ signal }) => apiGet(`/ai/insights/workspace/${workspaceId}?limit=3`, signal),
    enabled: !!workspaceId && aiInsightsAllowed,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
  const insights = useMemo(
    () => (insightsQuery.data?.insights || []).slice(0, 3),
    [insightsQuery.data]
  );
  const insightsLoading = insightsQuery.isLoading;

  const planName = useMemo(() => {
    const id = workspace?.plan_id || '';
    if (id.includes('studio')) return 'Studio';
    if (id.includes('pro'))    return 'Pro';
    return 'Free';
  }, [workspace?.plan_id]);

  // Top 3 most-recently-created films for the slate. (films payload doesn't
  // include updated_at, so we sort by created_at — same ordering used elsewhere.)
  const slateFilms = useMemo(() => {
    return [...films]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 3);
  }, [films]);

  const canCreateFilm = usage?.films?.can_create !== false;

  // Sample film removal (M0.4)
  const [sampleToRemove, setSampleToRemove] = useState(null);
  const [removingSample, setRemovingSample] = useState(false);
  const confirmRemoveSample = async () => {
    if (!sampleToRemove) return;
    setRemovingSample(true);
    try {
      await axios.delete(`${API_BASE}/films/${sampleToRemove.id}`, { headers: getAuthHeaders() });
      track(EVENTS.SAMPLE_FILM_DELETED, { film_id: sampleToRemove.id, workspace_id: workspaceId });
      toast.success('Sample film removed');
      queryClient.invalidateQueries({ queryKey: qk.workspaceFilms(workspaceId) });
      queryClient.invalidateQueries({ queryKey: qk.workspaceUsage(workspaceId) });
      queryClient.invalidateQueries({ queryKey: qk.workspaceActivity(workspaceId, 20) });
      setSampleToRemove(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove the sample film');
    } finally {
      setRemovingSample(false);
    }
  };

  // Resolve workspace role for action-button gating
  const wsRole = workspace?.role;
  const canEditSettings = wsRole === 'owner' || wsRole === 'admin';

  /* ----- handlers ----- */

  const handleCreateFilm = async (e) => {
    e.preventDefault();
    if (!newFilmTitle.trim()) {
      toast.error('Please enter a film title');
      return;
    }
    if (usage && !usage.films?.can_create) {
      toast.error(`Film limit reached (${usage.films?.limit} films). Upgrade your plan to create more.`);
      return;
    }
    try {
      setCreating(true);
      const res = await axios.post(`${API_BASE}/workspaces/${workspaceId}/films`,
        {
          title: newFilmTitle.trim(),
          description: newFilmDescription.trim() || null,
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Film created');
      setCreateOpen(false);
      setNewFilmTitle('');
      setNewFilmDescription('');
      // [H] Refresh the relevant RQ caches for this workspace.
      queryClient.invalidateQueries({ queryKey: qk.workspaceFilms(workspaceId) });
      queryClient.invalidateQueries({ queryKey: qk.workspaceUsage(workspaceId) });
      queryClient.invalidateQueries({ queryKey: qk.workspaceActivity(workspaceId, 20) });
      // Refresh the global FilmContext so subsequent navigations
      // (e.g. opening Film Manage to delete) see the new film with
      // correct permissions. Without this, the cached films list is
      // stale → can_edit defaults to false → "no permission" error.
      try { invalidateFilms && invalidateFilms(); } catch (err) { console.warn('[WorkspaceHome] invalidateFilms failed:', err); }
      try { refreshFilmContext && refreshFilmContext(true); } catch (err) { console.warn('[WorkspaceHome] refreshFilmContext failed:', err); }
      // Script-first onboarding (Phase 1 Task 2): go straight to the film
      // dashboard with the script-upload dropzone auto-expanded.
      const created = res.data;
      if (created?.id) {
        navigate(`/workspaces/${workspaceId}/films/${created.id}?import=script`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create film');
    } finally {
      setCreating(false);
    }
  };

  const handleNewProduction = () => {
    if (!canCreateFilm) {
      setUpgradeModalOpen(true);
      return;
    }
    setCreateOpen(true);
  };

  /* ----- loading state ----- */

  if (loading && films.length === 0 && !workspace) {
    return (
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="workspace-home-loading">
        {/* Identity bar skeleton */}
        <div className="mb-8 rounded-2xl border border-border bg-card px-5 py-4 md:px-6 md:py-5">
          <div className="flex items-center gap-4 md:gap-5">
            <Skeleton className="size-12 md:size-14 rounded-xl shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-7 w-1/2" />
              <Skeleton className="h-3 w-2/3" />
            </div>
            <Skeleton className="size-9 w-24 rounded-xl shrink-0 hidden sm:block" />
          </div>
        </div>
        {/* KPI strip skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          <Skeleton className="h-[150px] rounded-2xl" />
          <Skeleton className="h-[150px] rounded-2xl hidden sm:block" />
          <Skeleton className="h-[150px] rounded-2xl hidden lg:block" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background" data-testid="workspace-home">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Billing alerts (past due / over limits — Phase 2 Task 4) */}
        <BillingAlertBanner />

        {/* Banners (Free plan upgrade) */}
        {workspace?.plan_id === 'plan_free' && (
          <div
            className="mb-8 p-4 rounded-2xl border border-warning/30 bg-warning/10 flex items-center justify-between gap-4 flex-wrap"
            data-testid="wh-upgrade-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="size-9 rounded-xl bg-warning/20 text-warning flex items-center justify-center shrink-0">
                <Crown className="size-5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-foreground">
                  You&rsquo;re on the {planName} plan
                </p>
                <p className="text-sm text-muted-foreground">
                  Upgrade to Pro to unlock AI Risk Insights and lift the {usage?.films?.limit ?? 1}-film cap.
                </p>
              </div>
            </div>
            <Button onClick={() => setUpgradeModalOpen(true)} size="sm" className="rounded-xl">
              <Sparkles className="size-4 mr-1.5" /> Upgrade to Pro
            </Button>
          </div>
        )}

        {/* Identity bar — workspace name, plan, meta, and primary actions */}
        <IdentityBar
          workspace={workspace}
          planName={planName}
          memberCount={workspace?.member_count ?? null}
          filmCount={films.length}
          onMembers={() => navigate(`/workspaces/${workspaceId}/members`)}
          onSettings={() => navigate(`/workspaces/${workspaceId}/settings`)}
          onCalendar={() => navigate(`/workspaces/${workspaceId}/calendar`)}
          canEditSettings={canEditSettings}
        />

        {/* KPI strip — Films · Coverage · Team Activity */}
        <section
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12"
          data-testid="wh-kpi-strip"
        >
          <KpiFilms usage={usage} planName={planName} retry={usageError ? () => usageQuery.refetch() : null} />
          <KpiCoverage films={films} />
          <KpiTeamActivity
            memberCount={workspace?.member_count ?? null}
            events={activity}
            loading={activityLoading}
          />
        </section>

        {/* Films */}
        <section className="mb-12" data-testid="wh-slate">
          <div className="flex items-end justify-between gap-4 mb-5">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
                Films
              </h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {films.length === 0
                  ? 'No productions yet'
                  : `${films.length} ${films.length === 1 ? 'production' : 'productions'}`}
              </p>
            </div>
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] uppercase text-primary hover:text-primary/80 transition-colors"
              data-testid="wh-view-library"
            >
              View All Library
              <ArrowUpRight className="size-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading && films.length === 0 ? (
              [0, 1, 2].map((i) => (
                <Skeleton key={i} className="aspect-[4/3] rounded-2xl" />
              ))
            ) : (
              <>
                {slateFilms.map((film) => (
                  <ProductionCard
                    key={film.id}
                    film={film}
                    workspaceId={workspaceId}
                    onRemoveSample={wsRole === 'owner' ? setSampleToRemove : undefined}
                  />
                ))}
                <NewProductionCard
                  onClick={handleNewProduction}
                  atLimit={!canCreateFilm}
                />
              </>
            )}
          </div>
        </section>

        {/* Bottom row: AI Insights + Activity */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AIInsightsCard
            allowed={aiInsightsAllowed}
            insights={insights}
            insightsLoading={insightsLoading}
            films={films}
          />
          <ActivityCard
            events={activity}
            loading={activityLoading}
            error={activityError}
          />
        </section>
      </main>

      {/* Create Film Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="wh-create-film-dialog">
          <DialogHeader>
            <DialogTitle>Create New Film</DialogTitle>
            <DialogDescription>
              Add a new film to {workspace?.name || 'this workspace'}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateFilm}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="film-title">Film Title</Label>
                <Input
                  id="film-title"
                  data-testid="wh-film-title-input"
                  placeholder="e.g., Echoes of Tomorrow"
                  value={newFilmTitle}
                  onChange={(e) => setNewFilmTitle(e.target.value)}
                  disabled={creating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="film-description">Description (optional)</Label>
                <Textarea
                  id="film-description"
                  placeholder="Brief description"
                  value={newFilmDescription}
                  onChange={(e) => setNewFilmDescription(e.target.value)}
                  disabled={creating}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating} data-testid="wh-submit-film">
                {creating ? 'Creating…' : 'Create Film'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upgrade modal */}
      <AlertDialog open={!!sampleToRemove} onOpenChange={(o) => { if (!o) setSampleToRemove(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove the sample film?</AlertDialogTitle>
            <AlertDialogDescription>
              “{sampleToRemove?.title}” and its scenes, shots and schedule will be deleted. It never counted
              against your production limit, so nothing else changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingSample}>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmRemoveSample(); }} disabled={removingSample}>
              {removingSample ? 'Removing…' : 'Remove sample'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UpgradeWorkspaceModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        workspaceId={workspaceId}
        workspaceName={workspace?.name}
        trigger={!canCreateFilm ? 'second_film' : null}
        onUpgradeComplete={async () => {
          usageQuery.refetch();
          try {
            const r = await axios.get(`${API_BASE}/workspaces/${workspaceId}`, { headers: getAuthHeaders() });
            setWorkspace(r.data);
          } catch { /* ignore */ }
        }}
      />
    </div>
  );
};

export default WorkspaceHome;
